import React, { useEffect, useState } from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { RosterCell } from '../../interfaces/shift/RosterCell';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { isInstanceStatus } from './instanceStatus';

type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusChipColor = (status: string): StatusColor => {
    switch (status) {
        case 'SCHEDULED':  return 'default';
        case 'WORKED':     return 'success';
        case 'ABSENT':     return 'error';
        case 'ON_LEAVE':   return 'warning';
        case 'HOLIDAY':    return 'info';
        case 'CANCELLED':  return 'secondary';
        default:           return 'default';
    }
};

const KNOWN_ERRORS = {
    CROSS_INSTITUTION_REFERENCE: 'override.errors.crossInstitution',
    'ShiftInstance not found': 'override.errors.instanceNotFound',
} as const;

type OverrideErrorKey = typeof KNOWN_ERRORS[keyof typeof KNOWN_ERRORS] | 'override.errors.unexpected';

/** Maps a known backend error to a translation key; other backend text is shown as sent. */
const parseBackendError = (err: unknown): { key: OverrideErrorKey } | { message: string } => {
    const axErr = err as any;
    const msg: string = axErr?.response?.data?.message || axErr?.response?.data || axErr?.message || '';
    for (const [code, key] of Object.entries(KNOWN_ERRORS)) {
        if (msg.includes(code)) return { key };
    }
    return msg ? { message: msg } : { key: 'override.errors.unexpected' };
};

interface Props {
    open: boolean;
    onClose: () => void;
    cell: RosterCell;
    institutionId: string;
    shifts: Shift[];
    onSaved: () => void;
}

const RosterCellOverrideDialog: React.FC<Props> = ({ open, onClose, cell, institutionId, shifts, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const errorText = (err: unknown) => {
        const parsed = parseBackendError(err);
        return 'key' in parsed ? t(parsed.key) : parsed.message;
    };

    const [selectedShiftId, setSelectedShiftId] = useState<string>(cell.shiftId ?? '');
    const [notes, setNotes]     = useState('');
    const [saving, setSaving]   = useState(false);
    const [shiftError, setShiftError] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedShiftId(cell.shiftId ?? '');
            setNotes('');
            setSaving(false);
            setShiftError('');
        }
    }, [open, cell]);

    const activeShifts = shifts.filter(s => s.isActive);

    const handleSave = async () => {
        if (!selectedShiftId) {
            setShiftError(t('override.selectShift'));
            return;
        }
        setSaving(true);
        try {
            await ShiftRosterService.overrideCell(institutionId, cell.instanceId, {
                newShiftId: selectedShiftId,
                notes: notes.trim() || undefined,
            });
            toast.showSuccess(t('override.saved'));
            onSaved();
        } catch (err) {
            toast.showApiError(err, errorText(err));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm(t('override.confirmReset'))) return;
        setSaving(true);
        try {
            await ShiftRosterService.resetCell(institutionId, cell.instanceId);
            toast.showSuccess(t('override.resetDone'));
            onSaved();
        } catch (err) {
            toast.showApiError(err, errorText(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('override.title')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {/* Summary */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            {cell.businessDate}
                        </Typography>
                        <Chip
                            label={isInstanceStatus(cell.status) ? t(`instanceStatus.${cell.status}`) : cell.status}
                            color={statusChipColor(cell.status)}
                            size="small"
                        />
                        {cell.isManualOverride && (
                            <Chip label={t('override.manualOverride')} size="small" color="warning" />
                        )}
                    </Stack>

                    {/* Shift select */}
                    <FormControl fullWidth required error={!!shiftError}>
                        <InputLabel id="override-shift-label">{t('fields.shift')}</InputLabel>
                        <Select
                            labelId="override-shift-label"
                            value={selectedShiftId}
                            label={t('fields.shift')}
                            onChange={e => { setSelectedShiftId(e.target.value); setShiftError(''); }}
                            inputProps={{ 'data-testid': 'override-shift-select' }}
                        >
                            {activeShifts.map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                            ))}
                        </Select>
                        {shiftError && <FormHelperText>{shiftError}</FormHelperText>}
                    </FormControl>

                    {/* Notes */}
                    <TextField
                        label={t('common:fields.notes')}
                        multiline
                        rows={3}
                        fullWidth
                        value={notes}
                        onChange={e => setNotes(e.target.value.slice(0, 500))}
                        helperText={`${notes.length}/500`}
                        inputProps={{ 'data-testid': 'override-notes', maxLength: 500 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                {cell.isManualOverride && (
                    <Button
                        onClick={handleReset}
                        color="warning"
                        disabled={saving}
                        data-testid="override-reset-btn"
                    >
                        {t('common:actions.reset')}
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    data-testid="override-save-btn"
                >
                    {saving ? t('common:status.saving') : t('override.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RosterCellOverrideDialog;
